import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { Role, Permission } from '../types';
import Loading from '../components/Loading/Loading';

type Status = 'idle' | 'saving' | 'success' | 'error';

const RoleManagementPage: React.FC = () => {
  const { securedRequest, loading: apiIsLoading } = useApi();

  const [roles, setRoles] = useState<Role[] | null>(null);
  const [initialRoles, setInitialRoles] = useState<Role[] | null>(null);
  const [permissions, setPermissions] = useState<Permission[] | null>(null);
  const [status, setStatus] = useState<Record<string, Status>>({});
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async (signal?: AbortSignal) => {
    const { data, error } = await securedRequest('get', '/api/admin/roles-with-permissions', null, signal);
    if (data) {
      setRoles(data);
      setInitialRoles(data);
    }
    if (error && !signal?.aborted) {
      setError('Failed to load roles.');
    }
  }, [securedRequest]);

  const fetchPermissions = useCallback(async (signal?: AbortSignal) => {
    const { data, error } = await securedRequest('get', '/api/admin/permissions', null, signal);
    if (data) {
      setPermissions(data);
    }
    if (error && !signal?.aborted) {
      setError('Failed to load permissions.');
    }
  }, [securedRequest]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      await Promise.all([
        fetchRoles(controller.signal),
        fetchPermissions(controller.signal)
      ]);
    };
    fetchData();
    return () => controller.abort();
  }, [fetchRoles, fetchPermissions]);

  const handlePermissionChange = (roleId: string, permission: string, checked: boolean) => {
    if (!roles) return;
    setRoles(roles.map(role => {
      if (role.id === roleId) {
        const newPermissions = checked
          ? [...(role.permissions || []), permission]
          : (role.permissions || []).filter(p => p !== permission);
        return { ...role, permissions: newPermissions };
      }
      return role;
    }));
  };

  const handleSaveChanges = async (roleId: string) => {
    if (!roles || !initialRoles) return;
    setStatus(prev => ({ ...prev, [roleId]: 'saving' }));

    const originalRole = initialRoles.find(r => r.id === roleId);
    const updatedRole = roles.find(r => r.id === roleId);

    if (!originalRole || !updatedRole) return;

    const originalPermissions = new Set(originalRole.permissions || []);
    const updatedPermissions = new Set(updatedRole.permissions || []);

    const permissionsToAdd = Array.from(updatedPermissions).filter(p => !originalPermissions.has(p));
    const permissionsToRemove = Array.from(originalPermissions).filter(p => !updatedPermissions.has(p));

    const addRequest = permissionsToAdd.length
      ? securedRequest('post', `/api/admin/roles/${roleId}/permissions`, { permissions: permissionsToAdd })
      : Promise.resolve({ error: null });
    
    const removeRequest = permissionsToRemove.length
      ? securedRequest('delete', `/api/admin/roles/${roleId}/permissions?permissions=${permissionsToRemove.join(',')}`)
      : Promise.resolve({ error: null });

    const [addResult, removeResult] = await Promise.all([addRequest, removeRequest]);

    if (addResult.error || removeResult.error) {
      setStatus(prev => ({ ...prev, [roleId]: 'error' }));
      setTimeout(() => setStatus(prev => ({ ...prev, [roleId]: 'idle' })), 3000);
    } else {
      setStatus(prev => ({ ...prev, [roleId]: 'success' }));
      setInitialRoles(roles); // Update the 'source of truth'
      setTimeout(() => {
        setStatus(prev => ({ ...prev, [roleId]: 'idle' }));
      }, 1500);
    }
  };

  const handleCancelChanges = (roleId: string) => {
    if (!initialRoles) return;
    setRoles(roles?.map(role => {
      if (role.id === roleId) {
        const originalRole = initialRoles.find(r => r.id === role.id);
        return originalRole || role;
      }
      return role;
    }) || null);
  };

  if (roles === null || permissions === null) {
    return <Loading />;
  }

  return (
    <div className="container mt-5">
      <h2>Manage Roles and Permissions</h2>
      <p>Assign permissions to existing roles.</p>

      {error && <div className="alert alert-danger mt-3">{error}</div>}

      <hr />

      <h2>Existing Roles</h2>
      {roles.map(role => {
        const originalRole = initialRoles?.find(r => r.id === role.id);
        const hasChanges = JSON.stringify(originalRole?.permissions?.sort()) !== JSON.stringify(role.permissions?.sort());
        const currentStatus = status[role.id] || 'idle';

        const isLocaliQAdminRole = role.name === 'LocaliQ-Partner-Admin'; // New variable for clarity
        const displayablePermissions = isLocaliQAdminRole
          ? permissions
          : permissions.filter(p => p.value !== 'apps:admin');

        return (
          <div key={role.id} className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">{role.name}</h5>
              <p className="card-text">{role.description}</p>
              <h6>Permissions</h6>
              <div className="row">
                {displayablePermissions.map(permission => (
                  <div key={permission.value} className="col-md-4">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`${role.id}-${permission.value}`}
                        // Always checked and disabled for 'LocaliQ-Partner-Admin' role
                        checked={isLocaliQAdminRole ? true : role.permissions?.includes(permission.value)}
                        onChange={isLocaliQAdminRole ? undefined : (e => handlePermissionChange(role.id, permission.value, e.target.checked))}
                        disabled={isLocaliQAdminRole ? true : (currentStatus === 'saving' || apiIsLoading)}
                      />
                      <label className="form-check-label" htmlFor={`${role.id}-${permission.value}`}>
                        {permission.value}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="d-flex justify-content-end gap-2 mt-3 align-items-center">
                {currentStatus === 'success' && <span className="text-success me-2">Saved!</span>}
                {currentStatus === 'error' && <span className="text-danger me-2">Failed to save.</span>}
                {hasChanges && currentStatus === 'idle' && !isLocaliQAdminRole && ( // Disable save/cancel for Admin role
                  <>
                    <button className="btn btn-sm btn-secondary" onClick={() => handleCancelChanges(role.id)}>Cancel</button>
                    <button className="btn btn-sm btn-primary" onClick={() => handleSaveChanges(role.id)}>Save Changes</button>
                  </>
                )}
                {currentStatus === 'saving' && (
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RoleManagementPage;